import { Link, useNavigate } from '@tanstack/react-router';
import { Command } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { http } from '@/lib/api/http';
import { isApiError } from '@/lib/api/errors';
import { Button } from '@/ui/button';
import { Field, Input } from '@/ui/form';

function Frame({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <main className="flex min-h-dvh items-center justify-center px-5 py-10">
      <div className="animate-in w-full max-w-sm space-y-6">
        <div className="space-y-1.5">
          <div className="mb-4 grid size-10 place-items-center rounded-xl bg-accent text-accent-fg"><Command className="size-5" aria-hidden /></div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="text-sm text-muted">{hint}</p>
        </div>
        {children}
      </div>
    </main>
  );
}

/** Step 1: ask for a link. The server answers the same way whether or not the address has an account. */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true); setError(null);
    try { await http.post('/auth/forgot-password', { email: email.trim() }, { public: true }); setSent(true); }
    catch (e) { setError(isApiError(e) ? t(`errors.${e.code}`) : t('errors.INTERNAL_ERROR')); }
    finally { setBusy(false); }
  }

  return (
    <Frame title={t('reset.forgotTitle')} hint={t('reset.forgotHint')}>
      {sent ? (
        <p role="status" className="rounded-md border border-success-border bg-success-soft px-3 py-2 text-sm text-success">{t('reset.sent')}</p>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); void submit(); }} className="space-y-4" noValidate>
          {error && <p role="alert" className="rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-sm text-danger">{error}</p>}
          <Field label={t('auth.email')} required>{(id) => <Input id={id} type="email" autoComplete="username" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />}</Field>
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy} disabled={!/^\S+@\S+\.\S+$/.test(email.trim())}>{t('reset.send')}</Button>
        </form>
      )}
      <Link to="/login" className="block text-center text-sm text-muted underline-offset-2 hover:underline">{t('sec.backToLogin')}</Link>
    </Frame>
  );
}

/** Step 2: the e-mailed link lands here with ?token=… and the person chooses a new password. */
export function ResetPasswordPage({ token }: { token?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mismatch = again.length > 0 && pw !== again;

  async function submit() {
    setBusy(true); setError(null);
    try { await http.post('/auth/reset-password', { token, newPassword: pw }, { public: true }); setDone(true); }
    catch (e) { setError(isApiError(e) ? (e.message || t(`errors.${e.code}`)) : t('errors.INTERNAL_ERROR')); }
    finally { setBusy(false); }
  }

  if (!token) {
    return <Frame title={t('reset.title')} hint={t('reset.noToken')}><Button asChild variant="primary" className="w-full"><Link to="/forgot-password">{t('reset.askAgain')}</Link></Button></Frame>;
  }
  return (
    <Frame title={t('reset.title')} hint={t('reset.hint')}>
      {done ? (
        <div className="space-y-4">
          <p role="status" className="rounded-md border border-success-border bg-success-soft px-3 py-2 text-sm text-success">{t('reset.done')}</p>
          <Button variant="primary" size="lg" className="w-full" onClick={() => void navigate({ to: '/login', replace: true } as never)}>{t('auth.signIn')}</Button>
        </div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); void submit(); }} className="space-y-4" noValidate>
          {error && <p role="alert" className="rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-sm text-danger">{error} <Link to="/forgot-password" className="underline">{t('reset.askAgain')}</Link></p>}
          <Field label={t('reset.newPassword')} required>{(id) => <Input id={id} type="password" autoComplete="new-password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} />}</Field>
          <Field label={t('reset.repeat')} error={mismatch ? t('reset.mismatch') : undefined} required>{(id) => <Input id={id} type="password" autoComplete="new-password" value={again} onChange={(e) => setAgain(e.target.value)} />}</Field>
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy} disabled={!pw || pw !== again}>{t('reset.submit')}</Button>
        </form>
      )}
    </Frame>
  );
}
